import { Link } from 'react-router-dom'
import type { CSSProperties } from 'react'
import { useFilamentSpools } from '../../hooks/useFilamentSpools'
import { useSettings } from '../../hooks/useSettings'
import { useMaintenanceSchedules, computeDueStatus } from '../../hooks/useMaintenance'
import { usePrinters } from '../../hooks/usePrinters'
import { Chip } from '../../components/ui'

interface ChipPillProps {
  to: string
  tone: 'danger' | 'warning' | 'default'
  label: string
}

function ChipPill({ to, tone, label }: ChipPillProps) {
  const linkStyle: CSSProperties = { textDecoration: 'none' }
  return <Link to={to} style={linkStyle}><Chip tone={tone}>{label}</Chip></Link>
}

export default function StatusRow() {
  const { data: spools } = useFilamentSpools()
  const { data: settings } = useSettings()
  const { data: schedules } = useMaintenanceSchedules()
  const { data: printers } = usePrinters()

  const threshold = settings?.low_filament_threshold_grams ?? 100
  const lowSpools = (spools ?? []).filter((s) => s.status === 'in-use' && s.current_weight_grams < threshold)

  const printerById = new Map((printers ?? []).map((p) => [p.id, p]))
  const overdue = (schedules ?? [])
    .filter((s) => s.is_active)
    .map((s) => {
      const printer = printerById.get(s.printer_id)
      const hours = printer ? Number(printer.total_print_hours) : 0
      return computeDueStatus(s, hours)
    })
    .filter((d) => d.isOverdue)

  if (lowSpools.length === 0 && overdue.length === 0) return null

  const rowStyle: CSSProperties = {
    display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap',
    marginBottom: 'var(--space-4)',
  }
  return (
    <div style={rowStyle}>
      {overdue.length > 0 && (
        <ChipPill to="/maintenance" tone="danger" label={`⚠ ${overdue.length} maintenance overdue`} />
      )}
      {lowSpools.length > 0 && (
        <ChipPill to="/prints/filament" tone="warning" label={`▾ ${lowSpools.length} spool${lowSpools.length === 1 ? '' : 's'} low`} />
      )}
    </div>
  )
}
